// React provider for SPA client-otel (fn-1.31).
//
// **Purpose**: a tiny React Context that owns the {@link ClientOtelHandle}
// and lets components call `useTrackUserAction()` from button click
// handlers. Without the provider mounted, the hook returns a no-op.
//
// **Lifecycle**: the provider asynchronously inits the handle once the
// user's `userId` (the SPA-side session correlator) is known. While
// init is pending OR if init fails, `trackUserAction` is a no-op and
// `wrapFetch` is identity. The SPA's UX is unaffected.

import {
  type ReactElement,
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  type ClientOtelHandle,
  type ClientSpan,
  type ClientSpanSink,
  initClientOtel,
  setActiveClientOtelHandle,
} from './client-otel.js';

interface ClientOtelContextShape {
  /** The handle. `null` while init is pending or after init failure. */
  readonly handle: ClientOtelHandle | null;
}

const ClientOtelContext = createContext<ClientOtelContextShape>({ handle: null });

export interface ClientOtelProviderProps {
  /**
   * The SPA-side session correlator (Cognito user id from
   * `trpc.whoAmI`). Drives `traceId` derivation. When `null` (auth
   * gate pending), the provider holds the no-op state.
   */
  readonly userId: string | null;
  /** Optional sink — defaults to the production beacon sink. */
  readonly sink?: ClientSpanSink;
  readonly children: ReactNode;
}

export function ClientOtelProvider(props: ClientOtelProviderProps): ReactElement {
  const [handle, setHandle] = useState<ClientOtelHandle | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (props.userId === null) {
      setHandle(null);
      setActiveClientOtelHandle(null);
      return;
    }
    const initOptions: Parameters<typeof initClientOtel>[0] = {
      sessionId: props.userId,
      ...(props.sink !== undefined ? { sink: props.sink } : {}),
    };
    void initClientOtel(initOptions).then((next) => {
      if (cancelled) return;
      setHandle(next);
      // fn-1.31 — publish the handle to the module-level registry so
      // the tRPC client's fetch wrapper picks it up. The handle is
      // cleared on unmount AND on userId change (e.g. logout).
      setActiveClientOtelHandle(next);
    });
    return () => {
      cancelled = true;
      // Clear the module-level handle on unmount so a stale handle
      // from a previous session doesn't survive a re-login.
      setActiveClientOtelHandle(null);
    };
  }, [props.userId, props.sink]);

  const value = useMemo<ClientOtelContextShape>(() => ({ handle }), [handle]);
  return <ClientOtelContext.Provider value={value}>{props.children}</ClientOtelContext.Provider>;
}

/**
 * Hook returning the live `ClientOtelHandle` (or `null` until init
 * resolves). Consumers MUST handle `null` — render-time access can
 * happen before init completes.
 */
export function useClientOtel(): ClientOtelHandle | null {
  return useContext(ClientOtelContext).handle;
}

/**
 * Hook returning a `trackUserAction` function. Returns a callable that
 * emits a span when the handle is available; no-op until then.
 *
 * Usage:
 * ```tsx
 * const track = useTrackUserAction();
 * <button onClick={() => { track({ name: 'spa.approval.approve', metadata: { approval_id } }); handleApprove(); }} />
 * ```
 */
export function useTrackUserAction(): (action: {
  readonly name: string;
  readonly metadata?: Readonly<Record<string, unknown>>;
  readonly level?: ClientSpan['level'];
  /**
   * Optional override for session-coupled actions (fn-1.31 round 6).
   * See `ClientOtelHandle.trackUserAction` for the trace-id
   * stitching semantics. Prime via `useClientOtel().primeSessionTrace(...)`
   * on mount of any view that fires session-coupled clicks.
   */
  readonly sessionId?: string;
}) => string | null {
  const handle = useClientOtel();
  return (action) => (handle === null ? null : handle.trackUserAction(action));
}
